import { failureListOptions, failureReason, type FailureSummary } from '../dead-letter-queue/entries'
import type { Tel } from '@w/tel'
import { isUnreadable, itemRowsOptions, readable, unreadableReason, type Unreadable } from '../dead-letter-queue/item-readers'
import { WorkspaceSyncStatus } from './workspace-sync-status'
import { LoadingSpinner } from '../shell/loading-spinner'
import { manyPageTranslationRowsOptions, type PageTranslationRow } from '../translation/page-translation-rows'
import { useUserSpaces } from '../router/authenticated-layout'
import { queryOptions } from '@w/react-query/db'
import { nativeTongueFor } from '../translation/native-language'
import { cardsListOptions } from '../router/workspace-queries'
import { pageRouteParams } from '../router/page-route-params'
import type { SpaceResource } from '../router/spaces-resources'
import { pageReadColumns, pageReadSchema, type PageRow } from '../db/page-read'
import { contentResources, SpaceQueries, useSpacesView } from '../db/spaces-queries'
import { useQuery, useSuspenseQuery } from '@w/react-query'
import { useState, type ReactElement } from 'react'
import { IconButton } from '@w/ui/components/icon-button'
import { ChevronLeftIcon } from '@w/ui/components/icon'
import { useTranslation } from '../i18n'
import {
  useLoaderData,
  useNavigate,
  useRouterContext,
  useSearch,
} from '../router/flashcards-fe.router-bindings'
import type { Flashcard, FlashcardEvent } from '../db/schema'
import { getCardContent, getCardType, type CardType } from './cards-content'
import { formatRelativeTime } from './relative-time'
import { pageBlocks } from '../pages/page-blocks'
import { CardManagement } from './card-management'
import { MyActivity } from './teacher-activity'
import { duplicateStageReviewsOptions } from './card-history'
import { reviewEventSchema } from '../db/card-history'

interface CardRow {
  spaceId: string
  pageId: string
  qid: string
  pageTitle: string
  content: string
  type: CardType | null
  stage: number
  repeatAt: Date | 'now'
  /** Derived from the latest retained review fact, including stage-zero reviews. */
  lastReviewedAt: Date | null
  history: FlashcardEvent[]
  /** One device per review event of the stage, in event order. */
  duplicateStages: { stageAt: number; devices: string[] }[]
}

export function CardsPage(): ReactElement | null {
  const ctx = useRouterContext()
  const { workspaceId, progressWorkspaceId } = useLoaderData({ from: '/$workspaceId/cards' })
  const { data, error } = useSpacesView(useUserSpaces(), workspaceId, ctx.queryClient)
  if (error) throw error
  if (!data) return null
  if (!data.resources.some(resource => resource.space.workspaceId === progressWorkspaceId)) return <p>Progress unavailable</p>
  const resource = data.resources.find(resource => resource.space.workspaceId === progressWorkspaceId)
  return resource ? <CardsContent key={resource.connectionId} resource={resource} /> : null
}

function CardsContent({ resource }: { resource: SpaceResource }) {
  const ctx = useRouterContext()
  const { workspaceId } = useLoaderData({ from: '/$workspaceId/cards' })
  const { db, queryClient } = resource
  const { view } = useSearch({ from: '/$workspaceId/cards' })
  const failures = useQuery(failureListOptions(resource, ctx.tel), queryClient)
  const { data: cardItems } = useSuspenseQuery(cardsListOptions(resource), queryClient)
  const flashcards = readable(cardItems)
  const { data } = useSpacesView(useUserSpaces(), workspaceId, ctx.queryClient)
  const [user] = useState(() => ctx.auth.getUser())
  const reviews = useQuery(queryOptions(db.query`SELECT ${db.flashcardEvents.all} FROM ${db.flashcardEvents}
    WHERE ${db.flashcardEvents.userId.eq(user.id)} ORDER BY ${db.flashcardEvents.occurredAt}, ${db.flashcardEvents.id}`, db.flashcardEvents.__selectSchema), queryClient)
  const duplicates = useQuery(duplicateStageReviewsOptions(db,user.id),queryClient)
  const language = nativeTongueFor(user.settings.lexicon?.nativeLanguage, ctx.defaultNativeLanguage)
  if (view === 'activity') return <MyActivity resource={resource} />
  function pagesOptions(value: SpaceResource) {
    const { db, space } = value
    const options = itemRowsOptions(value, db.query`SELECT ${pageReadColumns(db.pages)} FROM ${db.pages}
      WHERE ${db.pages.id.inArray(flashcards.map(card => card.pageId))} AND ${db.pages.deletedAt.isNull()} ORDER BY ${db.pages.id}`, pageReadSchema(space.id), 'page', 'PAGE_DOCUMENT')
    return { ...options, queryKey: [...options.queryKey, 'with-translations', language], queryFn: async (qctx?: { tel?: Tel }) => {
      const items = await options.queryFn(qctx)
      const pages = readable(items)
      const { data: rows } = await ctx.queryClient.load(manyPageTranslationRowsOptions(ctx, pages, language))
      return [...pages.map(page => Object.assign({}, page, { translationRows: rows.filter(row => row.pageId === page.id) })), ...items.filter(item => isUnreadable(item))]
    } }
  }
  return <SpaceQueries resources={data ? contentResources(data) : []} options={pagesOptions}>
    {results => results.some(result => result.data === undefined && !result.error) ? <LoadingSpinner /> : <>
      <WorkspaceSyncStatus workspaceId={workspaceId} />
      <CardManagement workspaceId={workspaceId} />
      {duplicates.error ? <p role="alert">{duplicates.error.message}</p> : null}
      {data?.unavailable.map(({ space }) => <p key={space.id} role="alert">Incomplete workspace: {space.name} unavailable</p>)}
      {results.filter(result => result.error).map(result => <p key={result.resource.space.id} role="alert">{result.resource.space.name}: {result.error?.message}</p>)}
      <CardsTable rows={buildCardRows(flashcards, readable(results.flatMap(result => result.data ?? [])), reviews.data ?? [], duplicates.data ?? [])} failures={failures.error ? [] : failures.data ?? []} unreadable={[...cardItems.filter(item => isUnreadable(item)), ...results.flatMap(result => result.data ?? []).filter(item => isUnreadable(item))]} flashcards={flashcards} />
    </>}
  </SpaceQueries>
}

function CardsTable({ rows, failures, unreadable, flashcards }: { rows: CardRow[]; failures: FailureSummary[]; unreadable: Unreadable[]; flashcards: Flashcard[] }) {
  const letterRows = failures.filter(entry => entry.status === 'failed' && entry.category === 'derived').flatMap(entry => {
    const row = (qid: string) => ({ qid, key: `${qid}:${entry.id.join('.')}`, reason: failureReason(entry.reasons[0] ?? ''), detail: `${qid} · ${entry.id.join('.')}` })
    if (entry.source && 'Card' in entry.source) return [row(entry.source.Card.qid)]
    if (entry.source && 'Page' in entry.source) {
      const pageId = entry.source.Page.id
      return flashcards.filter(card => card.pageId === pageId).map(card => row(card.qid))
    }
    return []
  })
  // Placeholders come from the read result: undecodable cards, and cards of undecodable pages.
  const placeholderRows = unreadable.flatMap(item => item.unreadable === 'CARD_DATA'
    ? [{ qid: item.key, key: `${item.key}:read`, reason: unreadableReason(item.unreadable), detail: item.key }]
    : flashcards.filter(card => card.pageId === item.key).map(card => ({ qid: card.qid, key: `${card.qid}:read`, reason: unreadableReason(item.unreadable), detail: card.qid })))
  const failedRows = [...letterRows, ...placeholderRows.filter(row => !letterRows.some(letter => letter.qid === row.qid))]
  const failedQids = new Set(failedRows.map(row => row.qid))
  const healthyRows = rows.filter(row => !failedQids.has(row.qid))
  const ctx = useRouterContext()
  const navigate = useNavigate()
  const { t } = useTranslation('cards')

  function handleOpen(row: CardRow) {
    navigate({
      to: '/$workspaceId/spaces/$spaceId/pages/$pageId',
      params: pageRouteParams(ctx, row.spaceId, row.pageId),
      search: { qid: row.qid },
    })
  }

  return (
    <main className="bg-surface-1 text-on-surface min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-7xl flex-col gap-4 p-4">
        <header className="grid grid-cols-[2.5rem_1fr_2.5rem] items-center gap-3">
          <IconButton
            data-testid="flashcards-cards-back"
            semanticRole="secondary"
            variant="text"
            onClick={() => {
              navigate({ to: '/' })
            }}
          >
            {[<ChevronLeftIcon key="icon" />, t('back')]}
          </IconButton>
          <h1 className="text-center text-lg font-medium select-none">{t('title')}</h1>
          <span aria-hidden="true" />
        </header>

        {healthyRows.length === 0 && failedRows.length === 0 ? (
          <p
            data-testid="flashcards-cards-empty"
            className="text-on-surface-secondary text-sm"
          >
            {t('empty')}
          </p>
        ) : (
          <div className="border-separator bg-surface-2 overflow-auto rounded-md border">
            <table
              data-testid="flashcards-cards-table"
              className="w-full border-collapse text-left text-sm"
            >
              <thead>
                <tr className="border-separator text-on-surface-secondary border-b text-xs tracking-wide uppercase">
                  <th className="px-4 py-3 font-medium select-none">
                    {t('colPage')}
                  </th>
                  <th className="px-4 py-3 font-medium select-none">
                    {t('colContent')}
                  </th>
                  <th className="px-4 py-3 font-medium select-none">
                    {t('colType')}
                  </th>
                  <th className="px-4 py-3 font-medium select-none">
                    {t('colStage')}
                  </th>
                  <th className="px-4 py-3 font-medium select-none">
                    {t('colReviewIn')}
                  </th>
                  <th className="px-4 py-3 font-medium select-none">
                    {t('colLastReviewed')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {failedRows.map(({ qid, key, reason, detail }) => <tr key={key} data-testid={`flashcards-cards-failed-${qid}`} className="border-separator border-b last:border-b-0">
                  <td colSpan={6} className="px-4 py-3"><p>{reason}</p><p className="text-on-surface-secondary text-xs break-all">{detail}</p></td>
                </tr>)}
                {healthyRows.map((row) => (
                  <tr
                    key={row.qid}
                    data-testid={`flashcards-cards-row-${row.qid}`}
                    className="border-separator hover:bg-surface-2 cursor-pointer border-b select-none last:border-b-0"
                    onClick={({target:eventTarget}) => {
                      if (!(eventTarget instanceof Element) || !eventTarget.closest('details')) handleOpen(row)
                    }}
                  >
                    <td className="text-on-surface-secondary px-4 py-3">
                      {row.pageTitle}
                    </td>
                    <td className="max-w-md truncate px-4 py-3">
                      {row.content || t('noContent')}
                    </td>
                    <td className="text-on-surface-secondary px-4 py-3 uppercase">
                      {row.type ?? '—'}
                    </td>
                    <td className="text-on-surface-secondary px-4 py-3">
                      {row.stage}
                    </td>
                    <td className="text-on-surface-secondary px-4 py-3">
                      {row.repeatAt === 'now' ? 'Now' : formatRelativeTime(row.repeatAt)}
                    </td>
                    <td className="text-on-surface-secondary px-4 py-3">
                      {row.lastReviewedAt
                        ? <span>{formatRelativeTime(row.lastReviewedAt)}</span>
                        : t('neverReviewed')}
                      {row.history.length > 0 ? <details className="mt-2">
                        <summary data-testid={`card-history-${row.qid}`} className="cursor-pointer">Review history</summary>
                        {row.duplicateStages.map(group=><p key={group.stageAt}>Reviewed {group.devices.length === 2 ? 'twice' : `${group.devices.length} times`} at stage {group.stageAt} ({group.devices.map(deviceId => `device ${deviceId}`).join(', ')})</p>)}
                        <ul className="mt-2 space-y-1">{row.history.map(event=><li key={event.id}>Stage {event.stageAt} · device {event.deviceId} · {event.occurredAt.toISOString()}</li>)}</ul>
                      </details> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  )
}

function buildCardRows(
  flashcards: Flashcard[],
  pages: (PageRow & { translationRows: PageTranslationRow[] })[],
  reviews: FlashcardEvent[],
  duplicates: {qid:string;stageAt:number;deviceId:string}[],
): CardRow[] {
  const pagesById = new Map(pages.map(page => [page.id,page]))
  const latestReviews = new Map<string, { eventId: string; occurredAt: number }>()
  for (const event of reviews) {
    const parsed = reviewEventSchema.safeParse(event.data)
    if (!parsed.success) continue
    const review = parsed.data
    const prior = latestReviews.get(event.qid)
    if (!prior || review.occurredAt > prior.occurredAt || (review.occurredAt === prior.occurredAt && review.eventId > prior.eventId)) latestReviews.set(event.qid, review)
  }

  return flashcards.map((flashcard) => {
    const page = pagesById.get(flashcard.pageId)
    const blocks = page ? pageBlocks(page.content) : []
    const latestReview = latestReviews.get(flashcard.qid)
    const history = reviews.filter(review => review.qid === flashcard.qid).toSorted((a,b)=>a.occurredAt.getTime()-b.occurredAt.getTime() || a.id.localeCompare(b.id))
    return {
      spaceId: page?.spaceId ?? '',
      pageId: flashcard.pageId,
      qid: flashcard.qid,
      pageTitle: pageTitle(page),
      content: getCardContent(blocks, flashcard.qid, page?.translationRows ?? []),
      type: getCardType(blocks, flashcard.qid),
      stage: flashcard.stage,
      repeatAt: flashcard.repeatAt,
      lastReviewedAt: latestReview ? new Date(latestReview.occurredAt) : null,
      history,
      duplicateStages: [...new Set(duplicates.filter(review=>review.qid===flashcard.qid).map(review=>review.stageAt))].map(stageAt => ({
        stageAt, devices: history.filter(review => review.stageAt === stageAt).map(review => review.deviceId),
      })),
    }
  })
}

function pageTitle(page: { title: string } | undefined): string {
  if (!page) return 'Page unavailable'
  const title = page.title.trim()
  return title && title.length > 0 ? title : 'Untitled page'
}
